import { LitElement, html, nothing } from 'lit';
import { live } from 'lit/directives/live.js';
import { openDialog } from './foundation/dialog.js';
import { toolNav } from './tool-nav.js';
import { readPreferences, writePreferences, type Theme } from '../core/preferences.js';
import { fileIdentity, type FileHandoff, type FileHandoffRequest, type HandoffAdmission } from '../core/file-handoff.js';
import { createVectorDocumentStore, type VectorDocumentMetadata } from '../core/vector-documents.js';
import { download } from './object-urls.js';
import { loadVectorHost, type VectorEditor, type VectorFormat, type VectorInput, type VectorOutput, type VectorStatus } from './vector-host.js';

const EMPTY: VectorStatus = { disposed:false, hasArtwork:false, hasDraft:false, working:false, hasPreview:false, recovery:'disabled', recoveryAvailable:false };
const NATIVE_MIME = 'application/vnd.vector-core.document+json';
const BITMAPS = ['image/png', 'image/jpeg', 'image/webp'];
const LABELS: Record<VectorFormat, string> = { document:'Editable Vector document', svg:'SVG', png:'PNG', webp:'WebP', jpeg:'JPEG' };

/** Shell presentation, Files and deliberate saved snapshots. Vector owns all editing. */
export class YVectorTool extends LitElement {
  static properties = {  notice:{state:true}, status:{state:true}, busy:{state:true}, ready:{state:true}, runtimeImportFailed:{state:true}, theme:{state:true}, warnings:{state:true}, name:{state:true}, records:{state:true}, selected:{state:true}, libraryOpen:{state:true}, format:{state:true}, scale:{state:true}, background:{state:true}, snapshot:{state:true} };
  private editor?: VectorEditor;
  private status = EMPTY;
  private ready = false;
  private runtimeImportFailed = false;
  private busy = false;
  private notice = '';
  private warnings: readonly string[] = [];
  private theme: Theme = 'system';
  private name = 'Untitled drawing';
  private records: readonly VectorDocumentMetadata[] = [];
  private selected?: VectorDocumentMetadata;
  private libraryOpen = false;
  private dialogInvoker?: HTMLElement;
  private format: VectorFormat = 'svg';
  private scale = '1';
  private background = '#ffffff';
  private snapshot = '';
  private active = true;
  private everHadWork = false;
  private generation = 0;
  private task?: AbortController;
  private mountAbort?: AbortController;
  private mounting?: Promise<void>;
  private remountRequested = false;
  private donorAbort?: AbortController;
  private store = createVectorDocumentStore();
  private parents: readonly string[] = [];
  private hostOpening = false;
  /** Host association lifetime only; this is not a document revision or clean flag. */
  private associationEpoch = 0;
  /** One transient public input correlation; never a document revision or clean flag. */
  private pendingBitmapInput?: { readonly token:string; readonly generation:number; readonly parents:readonly string[]; committed:boolean };
  private chromeHistory = (event: KeyboardEvent) => {
    if (!this.active || !this.isConnected || !this.editor || event.isComposing || event.altKey || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
    const path=event.composedPath();
    if(path.some(n=>n instanceof Element&&n.classList.contains('vector-editor-mount')))return; // Core owns its shadow-root chrome.
    if(path.some(n=>n instanceof Element&&(n.matches('input,textarea,select')||(n as HTMLElement).isContentEditable)))return;
    event.preventDefault();event.stopImmediatePropagation();
    if(this.querySelector('dialog[open]'))return; // Modal draft must not undo the document underneath.
    this.editor.history(event.shiftKey?'redo':'undo');
  };
  private focusChrome = (event: PointerEvent) => {
    if(!this.active||event.button!==0||event.composedPath().some(n=>n instanceof Element&&(n.classList.contains('vector-editor-mount')||n.matches('input,textarea,select,[contenteditable]'))))return;
    const target=(event.target as Element).closest<HTMLElement>('button,summary,[tabindex],dialog')??this;
    if(target&&!target.matches(':disabled'))target.focus({preventScroll:true});
  };
  createRenderRoot() { return this; }
  connectedCallback() {
    super.connectedCallback();
    this.tabIndex=-1;
    try { this.theme = readPreferences(localStorage).theme; } catch { /* Tab appearance remains usable. */ }
    window.addEventListener('y-theme-change', this.syncTheme);
    this.addEventListener('keydown',this.chromeHistory,true);
    this.addEventListener('pointerdown',this.focusChrome,true);
  }
  protected firstUpdated() { void this.ensureMounted(); }
  /** Route loading is separate from editor mounting. Broker admission stays synchronous. */
  public async whenReady(): Promise<void> {
    await this.updateComplete;
    while (!this.editor) {
      await this.ensureMounted();
      if (!this.isConnected || !this.active) throw new Error('Vector is no longer active.');
      if (!this.editor && !this.mounting) throw new Error('Vector editor could not be mounted.');
    }
  }
  private ensureMounted(): Promise<void> {
    if (this.editor) return Promise.resolve();
    if (this.mounting) return this.mounting;
    const task = this.mount();
    this.mounting = task.finally(() => {
      this.mounting = undefined;
      const retry = this.remountRequested; this.remountRequested = false;
      if (retry && this.isConnected && this.active && !this.editor) void this.ensureMounted();
    });
    return this.mounting;
  }
  disconnectedCallback() {
    this.closeDialogs();
    this.generation++; this.pendingBitmapInput = undefined; this.task?.abort(); this.mountAbort?.abort(); this.donorAbort?.abort(); this.store.close();
    window.removeEventListener('y-theme-change', this.syncTheme);
    this.removeEventListener('keydown',this.chromeHistory,true);
    this.removeEventListener('pointerdown',this.focusChrome,true);
    const editor = this.editor; this.editor = undefined;
    void editor?.dispose().catch(() => {});
    super.disconnectedCallback();
  }
  public hasOpenWork(): boolean { return this.everHadWork || this.busy || this.status.working || this.status.hasArtwork || this.status.hasDraft || this.status.hasPreview; }
  public setActive(active: boolean) {
    if (this.active === active) return;
    this.active = active;
    if (!active) {
      this.closeDialogs();
      this.generation++; this.task?.abort(); this.task = undefined; this.mountAbort?.abort();
      this.busy = false; this.hostOpening = false; this.pendingBitmapInput = undefined;
      this.editor?.cancel();
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && this.contains(focused)) focused.blur();
    } else if (!this.editor) {
      if (this.mounting) this.remountRequested = true; else void this.ensureMounted();
    }
  }
  private current(generation: number, controller?: AbortController) { return this.isConnected && this.active && generation === this.generation && !controller?.signal.aborted; }
  private onStatus = (status: VectorStatus) => {
    // No public revision exists. These signals never establish saved/clean state.
    if (status.hasArtwork || status.hasDraft || status.working || status.hasPreview) this.everHadWork = true;
    if (!this.hostOpening && (status.working || !status.hasArtwork)) {
      this.associationEpoch++; this.parents = []; this.selected = undefined; this.snapshot = ''; this.warnings = [];
    }
    // The public commit callback consumes the token before post-commit status.
    // Every other preview ending (Cancel/no-op/stale input) drops it without ancestry.
    if (!status.hasPreview && !status.working && !this.hostOpening) this.pendingBitmapInput = undefined;
    this.status = status;
  };
  private bitmapCommitted(inputToken: string) {
    const pending = this.pendingBitmapInput;
    if (!pending || pending.token !== inputToken || !this.current(pending.generation)) return;
    pending.committed = true;
    this.pendingBitmapInput = undefined;
    this.parents = pending.parents;
    this.associationEpoch++; this.selected = undefined; this.snapshot = '';
    this.everHadWork = true;
    this.notice = 'Bitmap trace applied as editable geometry. The source image stays unchanged.';
  }
  private async mount() {
    const generation = this.generation, controller = new AbortController(); this.mountAbort = controller;
    this.notice = ''; this.runtimeImportFailed = false;
    try {
      let host: Awaited<ReturnType<typeof loadVectorHost>>;
      try { host = await loadVectorHost(); if (host.VECTOR_UI_PRESENTATION_VERSION !== 4) throw new Error('This UI candidate requires its matching Vector presentation build.'); }
      catch (error) { this.runtimeImportFailed = true; throw error; }
      if (!this.isConnected || generation !== this.generation || controller.signal.aborted) return;
      const container = this.querySelector<HTMLElement>('.vector-editor-mount')!;
      const editor = await host.mountVectorEditor(container, { mode:'draw', recovery:false, signal:controller.signal, onStatus:this.onStatus,
        documentActions:{tools:()=>this.showPanel('#vector-tools-dialog'),open:()=>this.choose(),saveLocally:()=>this.showSave(),savedDrawings:()=>this.showLibrary(),export:()=>this.showExport()},
        onBitmapCommit: event => this.bitmapCommitted(event.inputToken),
        onExport: output => { if (this.active && this.isConnected) this.downloadOutput(output); },
        onError: error => { if (this.isConnected && this.active) this.notice = error.message; },
      });
      if (!this.isConnected || generation !== this.generation || controller.signal.aborted) { await editor.dispose(); return; }
      this.editor = editor; this.status = editor.getStatus(); this.ready = true;
      this.notice = '';
    } catch (error) {
      if (this.isConnected && !controller.signal.aborted) this.failure(error);
    } finally { if (this.mountAbort === controller) this.mountAbort = undefined; }
  }
  private syncTheme = (event: Event) => { const theme = (event as CustomEvent).detail; if (['light','dark','system'].includes(theme)) this.theme = theme; };
  private setTheme(event: Event) {
    this.theme = (event.target as HTMLSelectElement).value as Theme;
    if (this.theme === 'system') delete document.documentElement.dataset.theme; else document.documentElement.dataset.theme = this.theme;
    window.dispatchEvent(new CustomEvent('y-theme-change', { detail:this.theme }));
    try { writePreferences(localStorage, { ...readPreferences(localStorage), theme:this.theme }); } catch { /* Tab-only appearance. */ }
  }
  private failure(error: unknown) {
    if ((error as {name?:string})?.name === 'AbortError') return;
    const code = (error as {code?:string})?.code;
    this.notice = code === 'conflict' ? 'Another tab changed this saved drawing. Your current artwork is kept. Refresh the list to reopen that snapshot, or save a separate copy.'
      : code === 'quota' ? 'Device storage is full. The previous saved snapshot is kept. Download an editable Vector document instead.'
      : code === 'unavailable' ? 'Local drawing storage is unavailable. Download an editable Vector document instead.'
      : code === 'incompatible' ? 'This saved drawing uses an incompatible envelope or document. It has not been removed. Portable documents remain usable.'
      : String((error as {message?:string})?.message || 'This action could not finish. Your committed drawing is kept.').slice(0,1000);
  }
  private get blocked() { return !this.ready || this.busy || this.status.working || this.status.hasDraft || this.status.hasPreview; }
  private allowReplace(): boolean {
    return !this.hasOpenWork() || confirm('Replace this drawing and its current undo history? Save a snapshot locally or download an editable Vector document first if you want to keep it.');
  }
  private classify(file: File, action?: 'open' | 'trace'): VectorInput['kind'] | undefined {
    if (BITMAPS.includes(file.type) || /\.(png|jpe?g|webp)$/i.test(file.name)) return 'bitmap';
    if (action === 'trace') return undefined;
    if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) return 'svg';
    if (!action && (file.type === NATIVE_MIME || file.type === 'application/json' || /\.json$/i.test(file.name))) return 'document';
    return undefined;
  }
  private bitmapMime(file: File) {
    return BITMAPS.includes(file.type) ? file.type : /\.png$/i.test(file.name) ? 'image/png' : /\.webp$/i.test(file.name) ? 'image/webp' : 'image/jpeg';
  }
  private choose() { this.querySelector<HTMLInputElement>('#vector-open')!.click(); }
  private focusedInvoker(): HTMLElement | null {
    let active = document.activeElement;
    while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
    return active instanceof HTMLElement ? active : null;
  }
  private async showPanel(id: string, focus?: string) {
    if (!this.active || !this.isConnected) return;
    const invoker = this.focusedInvoker(), generation = this.generation;
    await this.updateComplete;
    if (!this.current(generation)) return;
    const dialog = this.querySelector<HTMLDialogElement>(id);
    if (!dialog) return;
    this.dialogInvoker = invoker ?? undefined;
    openDialog(dialog, invoker);
    if (focus) dialog.querySelector<HTMLElement>(focus)?.focus({preventScroll:true});
  }
  private async showLibrary() {
    this.libraryOpen = true;
    await this.showPanel('#vector-library-dialog');
    void this.refreshLibrary();
  }
  private async showSave() {
    if (this.blocked || !this.active) return;
    this.libraryOpen = true;
    await this.showPanel('#vector-library-dialog', '[aria-label="Drawing name"]');
    void this.refreshLibrary();
  }
  private async showExport() {
    if (this.blocked || !this.active) return;
    await this.showPanel('#vector-export-dialog');
  }
  private closePanel(id: string) { this.querySelector<HTMLDialogElement>(id)?.close(); }
  private panelClosed() {
    this.libraryOpen = false;
    const invoker = this.dialogInvoker; this.dialogInvoker = undefined;
    if (this.active && invoker?.isConnected && !invoker.closest('[hidden]')) invoker.focus({preventScroll:true});
  }
  private closeDialogs() {
    this.dialogInvoker = undefined;
    for (const dialog of this.querySelectorAll<HTMLDialogElement>('dialog[open]')) dialog.close();
  }
  private selectFile(file?: File) {
    if (!file || this.blocked || !this.active) return;
    const kind = this.classify(file);
    if (!kind) { this.notice = 'Choose an editable Vector JSON document, SVG, PNG, JPEG or WebP.'; return; }
    const max = (kind === 'bitmap' ? 16 : kind === 'svg' ? 4 : 8) * 1048576;
    if (!file.size || file.size > max) { this.notice = `This input must contain between 1 byte and ${max / 1048576} MiB.`; return; }
    if (kind !== 'bitmap' && !this.allowReplace()) return;
    void this.openFile(file, kind, [fileIdentity(file)]);
  }
  public receiveHandoff(handoff: FileHandoff): HandoffAdmission {
    if (!this.isConnected || !this.active || !this.editor) return { accepted:false, reason:'unavailable' };
    if (handoff.targetTool !== 'vector' || !['open','trace'].includes(handoff.action)) return { accepted:false, reason:'invalid-input' };
    if (this.blocked) return { accepted:false, reason:'busy' };
    const kind = this.classify(handoff.file, handoff.action as 'open' | 'trace');
    if (!kind || kind === 'document' || (handoff.action === 'open' && kind !== 'svg') || !handoff.file.size) return { accepted:false, reason:'invalid-input' };
    if (handoff.file.size > (kind === 'bitmap' ? 16 : 4) * 1048576) return { accepted:false, reason:'capacity' };
    if (kind !== 'bitmap' && !this.allowReplace()) return { accepted:false, reason:'incompatible-mode' };
    const file = handoff.file; // Retain synchronously; admission precedes reads/parsing.
    void this.openFile(file, kind, [handoff.fileId]);
    return { accepted:true };
  }
  private async openFile(file: File, kind: VectorInput['kind'], parents: readonly string[]) {
    const controller = new AbortController(), generation = ++this.generation; this.task?.abort(); this.task = controller;
    this.pendingBitmapInput = kind === 'bitmap' ? {token:crypto.randomUUID(), generation, parents:[...new Set([...this.parents, ...parents])], committed:false} : undefined;
    const pendingBitmap = this.pendingBitmapInput, inputToken = pendingBitmap?.token;
    this.busy = true; this.everHadWork = true; this.hostOpening = true; this.notice = kind === 'bitmap' ? 'Reading image for trace preview…' : 'Opening drawing…'; this.warnings = [];
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (!this.current(generation, controller)) return;
      const result = await this.editor!.open({ kind, bytes, filename:file.name.slice(0,255), mimeType:kind === 'bitmap' ? this.bitmapMime(file) : file.type }, {signal:controller.signal, ...(inputToken ? {inputToken} : {})});
      if (!this.current(generation, controller)) return;
      this.warnings = result.warnings;
      // A ready observer may Apply synchronously before open's promise resumes.
      if (result.status === 'opened') {
        this.parents = parents; this.selected = undefined; this.snapshot = ''; this.name = file.name.replace(/(?:\.vector)?\.json$|\.svg$/i,'') || 'Untitled drawing';
        this.notice = result.warnings.length ? `Opened with ${result.warnings.length} conversion${result.warnings.length === 1 ? '' : 's'}. Review the notices below.` : 'Opened. Original file unchanged.';
      } else if (!pendingBitmap?.committed) {
        if (this.editor!.getStatus().hasPreview) this.notice = 'Trace preview ready. Inspect it, then choose Apply inside the editor to commit editable geometry.';
        else {
          if (this.pendingBitmapInput?.token === inputToken) this.pendingBitmapInput = undefined;
          this.notice = 'Trace preview ended without adding geometry. Your existing drawing is kept.';
        }
      }
    } catch (error) {
      if (this.pendingBitmapInput?.token === inputToken) this.pendingBitmapInput = undefined;
      if (this.current(generation, controller)) this.failure(error);
    }
    finally { if (generation === this.generation) { this.busy = false; this.hostOpening = false; if (this.task === controller) this.task = undefined; } }
  }
  private downloadOutput(output: VectorOutput) {
    download(new Blob([new Uint8Array(output.bytes).buffer], {type:output.mimeType}), output.filename);
    this.notice = `Downloaded ${LABELS[output.format]} copy · ${output.width} × ${output.height}${['png','webp','jpeg'].includes(output.format) ? ' pixels' : ' page units'}. `;
  }
  private async exportCopy(target?: 'images' | 'metadata' | 'check') {
    if (this.blocked || !this.active) return;
    const controller = new AbortController(), generation = ++this.generation; this.task = controller; this.busy = true;
    const format = target === 'check' ? 'svg' : target ? 'png' : this.format;
    try {
      const output = await this.editor!.export(format, { filename:this.name.trim() || 'drawing', signal:controller.signal,
        ...(['png','webp','jpeg'].includes(format) ? {scale:Number(this.scale)} : {}), ...(format === 'jpeg' ? {background:this.background} : {}),
      });
      if (!this.current(generation, controller)) return;
      if (!target) { this.downloadOutput(output); return; }
      const file = new File([new Uint8Array(output.bytes).buffer], output.filename, {type:output.mimeType});
      this.donorAbort?.abort(); this.donorAbort = new AbortController();
      const destination = target === 'images' ? {targetTool:target, action:'process' as const} : {targetTool:target, action:'inspect' as const};
      this.dispatchEvent(new CustomEvent<FileHandoffRequest>('y-file-handoff', {bubbles:true, composed:true, detail:{ file, sourceTool:'vector', ...destination,
        provenance:{kind:'derived', parentFileIds:this.parents, operation:'vector.export'}, signal:this.donorAbort.signal,
      }}));
    } catch (error) {
      if (this.current(generation, controller)) {
        if (format === 'webp' && (error as {code?:string})?.code === 'UNSUPPORTED_ENCODER') this.notice = 'This browser could not export WebP. Choose PNG to download or send this drawing to Images.';
        else this.failure(error);
      }
    }
    finally { if (generation === this.generation) { this.busy = false; if (this.task === controller) this.task = undefined; } }
  }
  private async refreshLibrary() {
    const generation = this.generation;
    try { const records = await this.store.list(); if (this.isConnected && generation === this.generation) this.records = records; }
    catch (error) { if (this.isConnected && generation === this.generation) this.failure(error); }
  }
  private async save(copy: boolean) {
    if (this.blocked || !this.active || !this.name.trim()) return;
    const capturedName = this.name.trim(), target = this.selected, association = this.associationEpoch;
    const controller = new AbortController(), generation = ++this.generation; this.task = controller; this.busy = true;
    try {
      const output = await this.editor!.export('document', {signal:controller.signal});
      if (!this.current(generation, controller)) return;
      const record = !copy && target ? await this.store.update(target.id, target.revision, output.bytes, {signal:controller.signal})
        : await this.store.create(capturedName, output.bytes, {signal:controller.signal});
      if (!this.current(generation, controller)) return;
      const saved = `Saved a snapshot of “${record.name}” on this device. It was captured when Save was chosen; later edits are not saved automatically.`;
      if (association === this.associationEpoch) {
        this.selected = record; if (this.name.trim() === capturedName) this.name = record.name; this.snapshot = saved;
      }
      this.notice = association === this.associationEpoch ? saved : `${saved} Your current drawing has continued or changed; reopen the saved list to review that earlier snapshot.`;
      this.libraryOpen = true; await this.refreshLibrary();
    } catch (error) { if (this.current(generation, controller)) this.failure(error); }
    finally { if (generation === this.generation) { this.busy = false; if (this.task === controller) this.task = undefined; } }
  }
  private async reopen(record: VectorDocumentMetadata) {
    if (this.blocked || !this.active || !this.allowReplace()) return;
    const controller = new AbortController(), generation = ++this.generation; this.pendingBitmapInput = undefined; this.task = controller; this.busy = true; this.hostOpening = true;
    try {
      const saved = await this.store.read(record.id, {signal:controller.signal});
      if (!this.current(generation, controller)) return;
      await this.editor!.open({kind:'document', bytes:saved.bytes, filename:`${saved.metadata.name}.vector.json`, mimeType:NATIVE_MIME}, {signal:controller.signal});
      if (!this.current(generation, controller)) return;
      this.selected = saved.metadata; this.everHadWork = true; this.name = saved.metadata.name; this.parents = []; this.warnings = [];
      this.snapshot = `Reopened snapshot of “${this.name}”. Undo history starts here; later edits need an explicit save.`; this.notice = this.snapshot;
    } catch (error) { if (this.current(generation, controller)) this.failure(error); }
    finally { if (generation === this.generation) { this.busy = false; this.hostOpening = false; if (this.task === controller) this.task = undefined; } }
  }
  private async rename(record: VectorDocumentMetadata) {
    const name = prompt('New name for this saved drawing', record.name)?.trim();
    if (!name || !this.active || this.busy) return;
    try { const renamed = await this.store.rename(record.id, record.revision, name); if (!this.isConnected) return; if (this.selected?.id === record.id) { this.selected = renamed; this.name = renamed.name; } await this.refreshLibrary(); }
    catch (error) { if (this.isConnected) this.failure(error); }
  }
  private async deleteSaved(record: VectorDocumentMetadata) {
    if (!this.active || this.busy || !confirm(`Delete the saved snapshot “${record.name}” from this device? Your open drawing stays in this tab.`)) return;
    try { await this.store.delete(record.id, record.revision); if (!this.isConnected) return; if (this.selected?.id === record.id) { this.selected = undefined; this.snapshot = ''; } await this.refreshLibrary(); this.notice = 'Saved snapshot deleted. The open drawing remains in this tab.'; }
    catch (error) { if (this.isConnected) this.failure(error); }
  }
  private cancel() { this.generation++; this.task?.abort(); this.task = undefined; this.busy = false; this.hostOpening = false; this.editor?.cancel(); this.notice = 'Cancelled. Committed artwork is kept.'; }
  render() { return html`
    <main id="main-vector">
      <h1 class="visually-hidden" tabindex="-1">Vector</h1>
      <input id="vector-open" type="file" class="visually-hidden" tabindex="-1" accept=".json,.svg,.png,.jpg,.jpeg,.webp,application/json,image/svg+xml,image/png,image/jpeg,image/webp" aria-label="Open a Vector document, SVG or bitmap" @change=${(event:Event) => { const input = event.target as HTMLInputElement; this.selectFile(input.files?.[0]); input.value = ''; }}>
      <div class="vector-editor-mount" aria-label="Vector editor"></div>
      <div class="vector-feedback" ?hidden=${this.ready && !this.notice} role="status" aria-live="polite">${!this.ready ? this.notice || 'Loading Vector…' : this.notice}${!this.ready && this.notice ? this.runtimeImportFailed ? html`<span>Vector’s files are unavailable. Save work in other tools before reloading.</span><button class="rk-button" @click=${() => location.reload()}>Reload</button>` : html`<button class="rk-button" @click=${() => this.ensureMounted()}>Retry</button>` : nothing}${this.busy || this.status.working || this.status.hasPreview || this.status.hasDraft ? html`<button class="text-button" @click=${this.cancel}>Cancel</button>` : nothing}</div>
      ${this.warnings.length ? html`<details class="vector-warnings"><summary>${this.warnings.length} SVG conversion${this.warnings.length===1?'':'s'}</summary><ul aria-label="SVG conversion notices">${this.warnings.map(warning => html`<li>${warning}</li>`)}</ul></details>` : nothing}
      <dialog id="vector-library-dialog" class="vector-dialog" aria-labelledby="saved-title" @close=${this.panelClosed}>
        <div class="vector-dialog-heading"><h2 id="saved-title">Saved drawings</h2><button class="text-button" aria-label="Close saved drawings" @click=${() => this.closePanel('#vector-library-dialog')}>Close</button></div>
        <div class="vector-dialog-scroll">
          <p class="vector-help">Explicit snapshots on this device. Download an editable file for a copy outside the browser.</p>
          <div class="vector-detail-body"><label class="drawing-name">Drawing name<input aria-label="Drawing name" maxlength="160" .value=${live(this.name)} @input=${(event:Event) => this.name = (event.target as HTMLInputElement).value}></label>
            ${this.selected ? html`<button class="rk-button" aria-label="Update saved drawing" ?disabled=${this.blocked} @click=${() => this.save(false)}>Update snapshot</button>` : nothing}
            <button class="rk-button" ?disabled=${this.blocked} @click=${() => this.save(true)}>${this.selected ? 'Save a copy' : 'Save locally'}</button>
          </div>
          ${this.snapshot ? html`<p class="vector-help">${this.snapshot}</p>` : nothing}
          <div class="saved-list-heading"><h3>On this device</h3><button class="text-button" ?disabled=${this.busy} @click=${this.refreshLibrary}>Refresh</button></div>
          ${!this.records.length ? html`<p class="vector-help">No saved drawings.</p>` : nothing}
          <ul class="vector-saved-list">${this.records.map(record => html`<li><span><strong>${record.name}</strong><small>${new Date(record.updatedAt).toLocaleString()} · ${(record.byteLength/1024).toFixed(1)} KiB</small></span><button class="rk-button" aria-label=${`Reopen ${record.name}`} ?disabled=${this.blocked} @click=${() => this.reopen(record)}>Open</button><button class="text-button" aria-label=${`Rename ${record.name}`} ?disabled=${this.busy} @click=${() => this.rename(record)}>Rename</button><button class="text-button" aria-label=${`Delete ${record.name}`} ?disabled=${this.busy} @click=${() => this.deleteSaved(record)}>Delete</button></li>`)}</ul>
          ${this.notice ? html`<p class="vector-help" role="status">${this.notice}</p>`:nothing}
          <p class="vector-help">Saving does not enable automatic recovery. Browser storage can be cleared.</p>
        </div>
      </dialog>
      <dialog id="vector-export-dialog" class="vector-dialog" aria-labelledby="export-title" @close=${this.panelClosed}>
        <div class="vector-dialog-heading"><h2 id="export-title">Export a copy</h2><button class="text-button" aria-label="Close export" @click=${() => this.closePanel('#vector-export-dialog')}>Close</button></div>
        <div class="vector-dialog-scroll">
          <fieldset class="vector-format-choice"><legend>Format</legend><div>${Object.entries(LABELS).map(([value,label])=>html`<label><input type="radio" name="vector-export-format" .value=${value} .checked=${this.format===value} ?disabled=${this.busy} @change=${()=>this.format=value as VectorFormat}><span>${value==='document'?'Editable file':value==='jpeg'?'JPEG':label}</span></label>`)}</div></fieldset>
          <div class="vector-detail-body">
            ${['png','webp','jpeg'].includes(this.format) ? html`<label>Scale<input aria-label="Raster scale" type="number" min="0.01" max="64" step="0.25" ?disabled=${this.busy} .value=${live(this.scale)} @input=${(event:Event) => this.scale = (event.target as HTMLInputElement).value}></label>` : nothing}
            ${this.format==='jpeg' ? html`<label>Background<input aria-label="JPEG background" type="color" ?disabled=${this.busy} .value=${this.background} @input=${(event:Event) => this.background=(event.target as HTMLInputElement).value}></label>` : nothing}
          </div>
          <p class="vector-help">${this.format==='document'?'Editable native document. Reopening starts a new undo history.':this.format==='svg'?'SVG interchange. Export preserves the supported document features.':this.format==='jpeg'?'JPEG uses the background above. Up to 8192 pixels per side and 16 megapixels.':'Transparency is retained. Up to 8192 pixels per side and 16 megapixels.'}</p>
          ${this.format==='webp'?html`<p class="vector-help">WebP depends on this browser. Send a PNG to Images for another conversion path.</p>`:nothing}
          <details class="vector-send-options"><summary>Use in another tool</summary><div class="vector-detail-body"><button class="rk-button" ?disabled=${this.blocked} @click=${() => this.exportCopy('images')}>PNG → Images</button><button class="rk-button" ?disabled=${this.blocked} @click=${() => this.exportCopy('metadata')}>PNG → Metadata</button><button class="rk-button" ?disabled=${this.blocked} @click=${() => this.exportCopy('check')}>SVG → Check</button></div></details>
          ${this.notice ? html`<p class="vector-help">${this.notice}</p>`:nothing}
        </div>
        <div class="vector-dialog-footer"><button class="rk-button" @click=${() => this.closePanel('#vector-export-dialog')}>Cancel</button><button class="rk-button vector-download" ?disabled=${this.blocked} @click=${()=>this.exportCopy()}>Download ${this.format==='document'?'editable file':this.format.toUpperCase()}</button></div>
      </dialog>
      <dialog id="vector-tools-dialog" class="vector-dialog vector-tools-dialog" aria-labelledby="vector-tools-title" @close=${this.panelClosed}>
        <div class="vector-dialog-heading"><h2 id="vector-tools-title">tij.gr</h2><button class="text-button" aria-label="Close tools" @click=${()=>this.closePanel('#vector-tools-dialog')}>Close</button></div>
        <div class="vector-dialog-scroll">${toolNav('vector')}<label class="vector-appearance-setting">Appearance<select aria-label="Appearance" .value=${this.theme} @change=${this.setTheme}><option value="system">System</option><option value="light">Light</option><option value="dark">Dark</option></select></label></div>
      </dialog>
    </main>`; }
}
customElements.define('y-vector-tool', YVectorTool);
